import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiLayers, FiPlus, FiRefreshCw, FiSearch } from "react-icons/fi";
import {
  addInteriorCatalog,
  deleteInteriorCatalog,
  editInteriorCatalog,
  getInteriorCatalogDetails,
  getInteriorCatalogTypes,
  uploadMediaFiles,
} from "../../../api/partner/partnerFeatureApis";
import { getAuthUser } from "../../../admin/navigation/adminNavigation";
import { showToast } from "../../common/notification/Notification";
import CatalogCard from "./CatalogCard";
import CatalogFormModal from "./CatalogFormModal";
import CatalogDetailsModal from "./CatalogDetailsModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import useMyCatalogs from "./useMyCatalogs";
import { formatPriceForApi, isCatalogOwner } from "./catalogUtils";

const isSuccess = (res) => res?.statusType === "SUCCESS" || res?.status === "SUCCESS";
const errorText = (err, fallback) => err?.response?.data?.text || err?.message || fallback;

/** Interior Design partner workspace: the partner's own catalog portfolio. */
export default function PartnerInteriorDesignHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.accessToken || authState?.user?.token || authState?.user?.refreshToken;
  const userId = getAuthUser(authState)?.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [typeId, setTypeId] = useState("");
  const [catalogTypes, setCatalogTypes] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState({ open: false, loading: false, catalog: null });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { items, loading, loadingMore, error, hasMore, reload, loadMore, removeLocal } = useMyCatalogs({
    token,
    userId,
    keyword,
    typeId,
  });

  // Debounce the search box
  useEffect(() => {
    const timer = setTimeout(() => setKeyword(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!token) return;
    getInteriorCatalogTypes(token)
      .then((data) => setCatalogTypes(data?.catalogTypes || []))
      .catch(() => setCatalogTypes([]));
  }, [token]);

  const openAdd = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);

  // Dashboard quick action links here with ?add=1
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      openAdd();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, openAdd]);

  const openEdit = (catalog) => {
    setDetail({ open: false, loading: false, catalog: null });
    setEditing(catalog);
    setFormOpen(true);
  };

  const openDetails = async (catalog) => {
    setDetail({ open: true, loading: true, catalog });
    try {
      const data = await getInteriorCatalogDetails(token, catalog.id);
      setDetail({ open: true, loading: false, catalog: data?.catalog || catalog });
    } catch {
      // Fall back to the list data we already have
      setDetail({ open: true, loading: false, catalog });
    }
  };

  const askDelete = (catalog) => {
    setDetail({ open: false, loading: false, catalog: null });
    setPendingDelete(catalog);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await deleteInteriorCatalog(token, pendingDelete.id);
      if (isSuccess(res)) {
        showToast("Catalog deleted.", { type: "success" });
        removeLocal(pendingDelete.id);
        setPendingDelete(null);
      } else {
        showToast(res?.text || "Couldn't delete the catalog.", { type: "error" });
      }
    } catch (err) {
      showToast(errorText(err, "Couldn't delete the catalog."), { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async ({ id, typeId: selectedTypeId, price, description, websiteUrl, files }) => {
    try {
      let uploaded = [];
      if (files.length > 0) {
        const uploadRes = await uploadMediaFiles(token, files);
        uploaded = (uploadRes?.propertyImages || []).map((img) => ({
          imagePath: img.imagePath,
          image_url: img.image_url ?? img.imageUrl,
          home: false,
          active: true,
          deleted: false,
        }));
        if (uploaded.length === 0) return { ok: false, error: "Upload failed. Please try again." };
      }

      // The backend treats every image in the request as new, so edits send new files only.
      const payload = {
        price: formatPriceForApi(price),
        description: description.trim(),
        url: websiteUrl.trim(),
        type: { id: Number(selectedTypeId) },
        catalog_images: uploaded,
        catalog_videos: [],
      };

      const res = id
        ? await editInteriorCatalog(token, { ...payload, id })
        : await addInteriorCatalog(token, payload);

      if (!isSuccess(res)) return { ok: false, error: res?.text || "Couldn't save the catalog." };

      showToast(id ? "Catalog updated." : "Catalog published.", { type: "success" });
      setFormOpen(false);
      setEditing(null);
      reload();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: errorText(err, "Couldn't save the catalog.") };
    }
  };

  const filtersActive = Boolean(keyword || typeId);
  const clearFilters = () => {
    setSearchInput("");
    setKeyword("");
    setTypeId("");
  };

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h1 className="fs-4 fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiLayers className="text-primary" aria-hidden="true" /> My Catalogs
          </h1>
          <p className="text-muted mb-0 small">
            Showcase your interior design projects, pricing and photos to customers.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload} disabled={loading}>
            <FiRefreshCw className={loading ? "spin-icon" : ""} aria-hidden="true" /> Refresh
          </button>
          <button type="button" className="btn btn-primary btn-sm px-3 shadow-sm" onClick={openAdd}>
            <FiPlus aria-hidden="true" /> Add Catalog
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-4">
              <label htmlFor="catalog-filter-type" className="visually-hidden">
                Filter by category
              </label>
              <select
                id="catalog-filter-type"
                className="form-select form-select-sm"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
              >
                <option value="">All categories</option>
                {catalogTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-8">
              <label htmlFor="catalog-search" className="visually-hidden">
                Search catalogs
              </label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch className="text-muted" aria-hidden="true" />
                </span>
                <input
                  id="catalog-search"
                  type="search"
                  className="form-control border-start-0"
                  placeholder="Search by description or category…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {filtersActive && (
                  <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5" role="status" aria-live="polite">
          <div className="spinner-border text-primary" aria-hidden="true" />
          <p className="text-muted mt-2 small">Loading your catalogs…</p>
        </div>
      ) : error && items.length === 0 ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center flex-wrap gap-2" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={reload}>
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiLayers size={48} className="text-muted mb-3 opacity-50" aria-hidden="true" />
            <h2 className="fs-5 fw-semibold text-secondary">{filtersActive ? "No matching catalogs" : "No catalogs yet"}</h2>
            <p className="text-muted small mb-3">
              {filtersActive
                ? "Try a different search or category."
                : "Add your first interior design project to start showcasing your work."}
            </p>
            {filtersActive ? (
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" onClick={openAdd}>
                <FiPlus aria-hidden="true" /> Add Catalog
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="row g-3">
            {items.map((catalog) => (
              <div key={catalog.id} className="col-12 col-sm-6 col-xl-4">
                <CatalogCard
                  catalog={catalog}
                  canManage={isCatalogOwner(catalog, userId, true)}
                  busy={deleting && pendingDelete?.id === catalog.id}
                  onView={openDetails}
                  onEdit={openEdit}
                  onDelete={askDelete}
                />
              </div>
            ))}
          </div>
          {error && (
            <div className="alert alert-warning mt-3 small" role="alert">
              {error}
            </div>
          )}
          {hasMore && (
            <div className="text-center mt-4">
              <button type="button" className="btn btn-outline-primary btn-sm px-4" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                    Loading…
                  </>
                ) : (
                  "Load more"
                )}
              </button>
            </div>
          )}
        </>
      )}

      <CatalogFormModal
        open={formOpen}
        catalog={editing}
        catalogTypes={catalogTypes}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />
      <CatalogDetailsModal
        open={detail.open}
        loading={detail.loading}
        catalog={detail.catalog}
        canManage={isCatalogOwner(detail.catalog, userId, true)}
        onClose={() => setDetail({ open: false, loading: false, catalog: null })}
        onEdit={openEdit}
        onDelete={askDelete}
      />
      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        deleting={deleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
